import axios from 'axios'

// 注意：main.js 在模块导入完成后才设置 axios.defaults.baseURL，必须调用时读取
function getBaseURL () {
  return axios.defaults.baseURL || ''
}

/**
 * 会话列表（本人）
 * @return {Promise<Array>} 会话数组
 */
export async function fetchSessionList () {
  const { data: res } = await axios.get('assistant/session/list')
  if (res.meta.status !== 200) throw new Error(res.meta.msg)
  return res.data
}

/**
 * 会话消息（含工具轨迹，历史回放）
 * @param {string} sessionId 会话ID
 * @return {Promise<Array>} 消息数组
 */
export async function fetchSessionMessages (sessionId) {
  const { data: res } = await axios.get('assistant/session/messages', { params: { sessionId } })
  if (res.meta.status !== 200) throw new Error(res.meta.msg)
  return res.data
}

/**
 * 技能列表（官方/部门/我的）
 * @return {Promise<Array>} 技能数组
 */
export async function fetchSkillList () {
  const { data: res } = await axios.get('assistant/skill/list')
  if (res.meta.status !== 200) throw new Error(res.meta.msg)
  return res.data
}

/**
 * 存为技能（简版：从会话消息提取做法）
 * @param {Object} payload { sessionId, messageId, name, description?, visibility? }
 */
export async function saveSkill (payload) {
  return axios.post('assistant/skill/save', payload)
}

/**
 * 删除自己的技能
 * @param {string} skillId 技能ID
 */
export async function removeSkill (skillId) {
  return axios.post('assistant/skill/remove', { skillId })
}

/**
 * 消息反馈
 * @param {string} messageId 消息ID
 * @param {number} rating 1 赞 / -1 踩
 * @param {string} [reason] 踩的原因（选填）
 */
export async function feedback (messageId, rating, reason) {
  const { data: res } = await axios.post('assistant/feedback', { messageId, rating, reason })
  if (res.meta.status !== 200) throw new Error(res.meta.msg)
  return res.data
}

/**
 * 导出产物列表（下载中心页，本人，分页）
 * @param {Object} params { pagenum, pagesize }
 * @return {Promise<Object>} { total, list }
 */
export async function fetchArtifactList (params) {
  const { data: res } = await axios.get('assistant/artifact/list', { params })
  if (res.meta.status !== 200) throw new Error(res.meta.msg)
  return res.data
}

/**
 * 单个产物状态（导出卡片轮询/历史回放刷新）
 * @param {string} id 产物ID
 * @return {Promise<Object|null>} 产物文档；不存在/已过期/异常时为 null
 */
export async function fetchArtifactStatus (id) {
  const { data: res } = await axios.get('assistant/artifact/status', { params: { id } })
  if (res.meta.status !== 200) return null
  return res.data
}

/**
 * 发起导出（表格卡片「导出 Excel」按钮；与对话工具共用受理层与配额）
 * @param {string} tool 查询工具名
 * @param {Object} args 查询参数（与该工具入参一致；按当时参数重查最新数据）
 * @return {Promise<Object>} axios 响应（组件层判 meta.status，拒绝话术在 meta.msg）
 */
export function createArtifactExport (tool, args) {
  return axios.post('assistant/artifact/create', { tool, args })
}

/**
 * 下载产物（blob 流式落浏览器）
 * @param {string} id 产物ID
 * @param {string} [fallbackName] 响应头缺文件名时的兜底文件名
 */
export async function downloadArtifact (id, fallbackName) {
  const res = await axios.get('assistant/artifact/download', { params: { id }, responseType: 'blob' })
  // 归属/状态/过期错误时响应体是 JSON（HTTP 200 + meta），按业务错误抛出
  if (res.data && res.data.type === 'application/json') {
    let msg = '下载失败'
    try {
      msg = JSON.parse(await res.data.text()).meta.msg || msg
    } catch (err) {
      // 保留默认提示
    }
    throw new Error(msg)
  }
  let filename = fallbackName || '导出.xlsx'
  const cd = res.headers['content-disposition'] || ''
  const m = cd.match(/filename\*=UTF-8''([^;]+)/)
  if (m) {
    try {
      filename = decodeURIComponent(m[1])
    } catch (err) {
      // 保留兜底名
    }
  }
  const url = URL.createObjectURL(res.data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  // 延迟释放：Safari/WKWebView（企微 iOS webview）同步 revoke 会中断下载
  setTimeout(() => URL.revokeObjectURL(url), 3000)
}

/**
 * 管理页·用量统计（管理员）
 * @param {number} [days] 统计天数，默认 7
 * @return {Promise<Object>} { sessionCount, messageCount, tokens, toolCount, byDay, byTool }
 */
export async function fetchUsage (days) {
  const { data: res } = await axios.get('assistant/admin/usage', { params: { days } })
  if (res.meta.status !== 200) throw new Error(res.meta.msg)
  return res.data
}

/**
 * 管理页·工具调用审计（管理员，分页）
 * @param {Object} params { pagenum, pagesize, tool? }
 * @return {Promise<Object>} { total, list }
 */
export async function fetchAudit (params) {
  const { data: res } = await axios.get('assistant/admin/audit', { params })
  if (res.meta.status !== 200) throw new Error(res.meta.msg)
  return res.data
}

/**
 * 授权矩阵·工具目录 + 指定角色授权格（管理员）
 * @param {string} [roleId] 角色ID（缺省只取目录）
 * @return {Promise<Object>} { catalog: [{name,description,domain,scopeModes,defaultScopeMode,writeTier,version}], grants }
 */
export async function fetchRoleTools (roleId) {
  const { data: res } = await axios.get('assistant/admin/roleTools', { params: { roleId } })
  if (res.meta.status !== 200) throw new Error(res.meta.msg)
  return res.data
}

/**
 * 授权矩阵·保存角色授权格（管理员，全量替换）
 * @param {string} roleId 角色ID
 * @param {Array<Object>} grants [{ toolName, scopeMode }]
 */
export async function saveRoleTools (roleId, grants) {
  const { data: res } = await axios.post('assistant/admin/roleTools/save', { roleId, grants })
  if (res.meta.status !== 200) throw new Error(res.meta.msg)
  return res.data
}

/**
 * SSE 流式对话（fetch-stream，带 Authorization 头）
 * 事件：meta / tools / text-delta / tool-start / tool-result / done / error
 * @param {Object} params { sessionId?, text, skillId?, signal?, onEvent(event, payload) }
 */
export async function chatStream ({ sessionId, text, skillId, signal, onEvent }) {
  const token = window.sessionStorage.getItem('token')
  const res = await fetch(`${getBaseURL()}assistant/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: token || '' },
    body: JSON.stringify({ sessionId: sessionId || undefined, text, skillId: skillId || undefined }),
    signal
  })
  if (!res.ok) {
    let message = `请求失败（${res.status}）`
    try {
      const body = await res.json()
      if (body && body.meta && body.meta.msg) message = body.meta.msg
    } catch (err) {
      // 非 JSON 响应体，保留默认提示
    }
    throw new Error(message)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder('utf-8')
  let buffer = ''
  let done = false
  while (!done) {
    const chunk = await reader.read()
    done = chunk.done
    if (!chunk.value) continue
    buffer += decoder.decode(chunk.value, { stream: true })
    let sep = buffer.indexOf('\n\n')
    while (sep >= 0) {
      const block = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      dispatchBlock(block, onEvent)
      sep = buffer.indexOf('\n\n')
    }
  }
}

/**
 * 解析单个 SSE 事件块（event:/data: 行）
 */
function dispatchBlock (block, onEvent) {
  let event = 'message'
  const dataLines = []
  block.split('\n').forEach(line => {
    if (line.indexOf('event: ') === 0) {
      event = line.slice(7)
    } else if (line.indexOf('data: ') === 0) {
      dataLines.push(line.slice(6))
    }
  })
  if (!dataLines.length) return
  let payload
  try {
    payload = JSON.parse(dataLines.join('\n'))
  } catch (err) {
    return
  }
  onEvent(event, payload)
}
